import React, { useState, useEffect } from 'react';
import { Store, ChevronRight, Palette, Package2, CreditCard, ShoppingCart, Plus, X, ChevronDown, Search } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from '../components/toasts/SimpleToast';

export default function OnlineStore() {
  const { selectedBusiness } = useBusiness();

  // Store design settings state
  const [storeDesign, setStoreDesign] = useState({
    sidebarColor: '#374151', // Default gray-700
    sidebarTextColor: '#ffffff', // Default white
    headerColor: '#ffffff',  // Default white
    backgroundColor: '#f9fafb', // Default gray-50
    accentColor: '#3b82f6',   // Default blue-500
    businessNameColor: '#111827', // Default gray-900
    browseProductsColor: '#6b7280', // Default gray-500
    showBusinessLogo: true,   // Default show logo
    showBusinessName: true,   // Default show name
    showPopularProducts: false, // Default hide popular products
    popularProductIds: [] as string[],     // Default empty array
    displayStockLevels: false,   // Default hide stock levels
    hideOutOfStockProducts: false // Default show all products
  });

  // Shipping settings state
  const [shippingSettings, setShippingSettings] = useState({
    enableInStorePickup: true,
    pickupAddress: '',
    shippingMethods: [] as Array<{name: string, price: number}>
  });

  // UI state
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  const [isDesignExpanded, setIsDesignExpanded] = useState(false);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);

  // Popular products state
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Load store design and shipping settings
  const loadStoreSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .from('online_store_settings')
        .select('sidebar_color, sidebar_text_color, header_color, background_color, accent_color, business_name_color, browse_products_color, show_business_logo, show_business_name, show_popular_products, popular_product_ids, display_stock_levels, hide_out_of_stock_products, enable_in_store_pickup, pickup_address, shipping_methods')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found, use defaults
          return;
        }
        throw error;
      }

      if (data) {
        setStoreDesign({
          sidebarColor: data.sidebar_color || '#374151',
          sidebarTextColor: data.sidebar_text_color || '#ffffff',
          headerColor: data.header_color || '#ffffff',
          backgroundColor: data.background_color || '#f9fafb',
          accentColor: data.accent_color || '#3b82f6',
          businessNameColor: data.business_name_color || '#111827',
          browseProductsColor: data.browse_products_color || '#6b7280',
          showBusinessLogo: data.show_business_logo ?? true,
          showBusinessName: data.show_business_name ?? true,
          showPopularProducts: data.show_popular_products ?? false,
          popularProductIds: data.popular_product_ids || [],
          displayStockLevels: data.display_stock_levels ?? false,
          hideOutOfStockProducts: data.hide_out_of_stock_products ?? false
        });
        
        // Load shipping settings
        setShippingSettings({
          enableInStorePickup: data.enable_in_store_pickup ?? true,
          pickupAddress: data.pickup_address || '',
          shippingMethods: data.shipping_methods || []
        });
      }
    } catch (error) {
      console.error('Error loading store settings:', error);
      toast.error({
        title: "Error",
        description: "Failed to load store settings",
        duration: 3000,
      });
    }
  };

  // Save store design settings
  const saveStoreDesignSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSavingDesign(true);
      console.log('Saving design settings for business:', selectedBusiness.id);
      console.log('Design data:', storeDesign);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      console.log('Existing data check:', { existingData, selectError });

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      const settingsData = {
        business_id: selectedBusiness.id,
        sidebar_color: storeDesign.sidebarColor,
        sidebar_text_color: storeDesign.sidebarTextColor,
        header_color: storeDesign.headerColor,
        background_color: storeDesign.backgroundColor,
        accent_color: storeDesign.accentColor,
        business_name_color: storeDesign.businessNameColor,
        browse_products_color: storeDesign.browseProductsColor,
        show_business_logo: storeDesign.showBusinessLogo,
        show_business_name: storeDesign.showBusinessName,
        show_popular_products: storeDesign.showPopularProducts,
        popular_product_ids: storeDesign.popularProductIds,
        display_stock_levels: storeDesign.displayStockLevels,
        hide_out_of_stock_products: storeDesign.hideOutOfStockProducts
      };

      console.log('Settings data to save:', settingsData);

      if (existingData) {
        console.log('Updating existing settings...');
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(settingsData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        // Create new settings record
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(settingsData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Design settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving store design settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save design settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingDesign(false);
    }
  };

  // Save shipping settings
  const saveShippingSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    try {
      setIsSavingShipping(true);
      console.log('Saving shipping settings for business:', selectedBusiness.id);
      console.log('Shipping data:', shippingSettings);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      const shippingData = {
        business_id: selectedBusiness.id,
        enable_in_store_pickup: shippingSettings.enableInStorePickup,
        pickup_address: shippingSettings.pickupAddress || null,
        shipping_methods: shippingSettings.shippingMethods
      };

      if (existingData) {
        console.log('Updating existing settings...');
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(shippingData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Shipping settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(shippingData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Shipping settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Shipping settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving shipping settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save shipping settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingShipping(false);
    }
  };

  // Shipping methods management
  const addShippingMethod = () => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: [...prev.shippingMethods, { name: '', price: 0 }]
    }));
  };

  const updateShippingMethod = (index: number, field: 'name' | 'price', value: string | number) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.map((method, i) => 
        i === index ? { ...method, [field]: value } : method
      )
    }));
  };

  const removeShippingMethod = (index: number) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.filter((_, i) => i !== index)
    }));
  };

  // Load available products for popular products selection
  const loadAvailableProducts = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setIsLoadingProducts(true);
      const { data, error } = await supabase
        .from('stock_items')
        .select('id, name, description, price, quantity')
        .eq('business_id', selectedBusiness.id)
        .order('name');

      if (error) throw error;
      setAvailableProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Toggle product selection for popular products
  const toggleProductSelection = (productId: string) => {
    setStoreDesign(prev => {
      const currentIds = prev.popularProductIds || [];
      if (currentIds.includes(productId)) {
        // Remove product
        return {
          ...prev,
          popularProductIds: currentIds.filter(id => id !== productId)
        };
      } else if (currentIds.length < 10) {
        // Add product (max 10)
        return {
          ...prev,
          popularProductIds: [...currentIds, productId]
        };
      }
      return prev;
    });
  };

  // Load settings on component mount
  useEffect(() => {
    loadStoreSettings();
  }, [selectedBusiness?.id]);

  // Load products when popular products is enabled
  useEffect(() => {
    if (storeDesign.showPopularProducts && availableProducts.length === 0) {
      loadAvailableProducts();
    }
  }, [storeDesign.showPopularProducts]);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Online Store</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your customer-facing online store</p>
            </div>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
              Configure Store
            </button>
          </div>

          {/* Store Status */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Store className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Store Status</h2>
                  <p className="text-sm text-gray-600">Your online store is ready to accept orders</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-600">Active</span>
              </div>
            </div>
            
            {/* Store URL */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-white rounded-lg px-3 py-2 border border-gray-300">
                  <code className="text-sm text-blue-600">
                    {window.location.origin}/{selectedBusiness?.slug || 'your-business'}/store
                  </code>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => {
                      const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                      window.open(storeUrl, '_blank');
                    }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Preview
                  </button>
                  <button 
                    onClick={() => {
                      const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                      navigator.clipboard.writeText(storeUrl).then(() => {
                        toast.success({
                          title: 'Success',
                          description: 'Store link copied to clipboard!',
                          duration: 3000
                        });
                      }).catch(() => {
                        // Fallback for older browsers
                        const textArea = document.createElement('textarea');
                        textArea.value = storeUrl;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        toast.success({
                          title: 'Success',
                          description: 'Store link copied to clipboard!',
                          duration: 3000
                        });
                      });
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Store Configuration */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Store Configuration</h2>
              <p className="text-sm text-gray-600 mt-1">Configure your online store settings and appearance</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Store Design Section - Will be expanded in next update */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsDesignExpanded(!isDesignExpanded)}
                      className="w-full flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Palette className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Store Design</h3>
                          <p className="text-sm text-gray-600">Customize colors, logo, and layout</p>
                        </div>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isDesignExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                  
                  {isDesignExpanded && (
                    <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                      <p className="text-sm text-gray-500 text-center py-4">
                        Complete design settings will be added in the next update...
                      </p>
                      
                      {/* Save Button */}
                      <div className="pt-2 border-t border-gray-200">
                        <button 
                          onClick={saveStoreDesignSettings}
                          disabled={isSavingDesign}
                          className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingDesign ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Design Changes'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Catalog Section */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsCatalogExpanded(!isCatalogExpanded)}
                      className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Package2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Product Catalog</h3>
                          <p className="text-sm text-gray-600">Manage categories and visibility</p>
                        </div>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isCatalogExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                  
                  {isCatalogExpanded && (
                    <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                      {/* Stock Display Settings */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">Customer Display Settings</h4>
                        
                        {/* Display Stock Levels */}
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Display Stock Levels
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Show stock quantities to customers on the store
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={storeDesign.displayStockLevels}
                            onChange={(e) => setStoreDesign(prev => ({ ...prev, displayStockLevels: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </div>

                        {/* Hide Out of Stock Products */}
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Hide Out of Stock Products
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Don't show products with zero stock to customers
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={storeDesign.hideOutOfStockProducts}
                            onChange={(e) => setStoreDesign(prev => ({ ...prev, hideOutOfStockProducts: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <div className="pt-2 border-t border-gray-200">
                        <button 
                          onClick={saveStoreDesignSettings}
                          disabled={isSavingDesign}
                          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingDesign ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Catalog Settings'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Settings Section */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsShippingExpanded(!isShippingExpanded)}
                      className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CreditCard className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Payment Settings</h3>
                          <p className="text-sm text-gray-600">Configure payment methods and shipping</p>
                        </div>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isShippingExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {isShippingExpanded && (
                    <div className="px-4 pb-4 space-y-6">
                      {/* Shipping Options Section */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Shipping Options</h4>
                        
                        {/* In-Store Pickup */}
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="enablePickup"
                              checked={shippingSettings.enableInStorePickup}
                              onChange={(e) => setShippingSettings(prev => ({
                                ...prev,
                                enableInStorePickup: e.target.checked
                              }))}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor="enablePickup" className="text-sm font-medium text-gray-700">
                              In-Store Pickup
                            </label>
                          </div>
                          
                          {shippingSettings.enableInStorePickup && (
                            <div className="ml-7 space-y-2">
                              <p className="text-xs text-gray-600">
                                Using business address: <span className="font-medium">{selectedBusiness?.address || 'No address set'}</span>
                              </p>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Custom pickup address (optional)
                                </label>
                                <input
                                  type="text"
                                  value={shippingSettings.pickupAddress}
                                  onChange={(e) => setShippingSettings(prev => ({
                                    ...prev,
                                    pickupAddress: e.target.value
                                  }))}
                                  placeholder="Enter custom pickup address..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Additional Shipping Methods */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Additional Shipping Methods</h4>
                          <button
                            onClick={addShippingMethod}
                            className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center space-x-1"
                          >
                            <Plus className="h-4 w-4" />
                            <span>Add Method</span>
                          </button>
                        </div>
                        
                        {shippingSettings.shippingMethods.length === 0 ? (
                          <p className="text-sm text-gray-500 italic">No additional shipping methods configured</p>
                        ) : (
                          <div className="space-y-3">
                            {shippingSettings.shippingMethods.map((method, index) => (
                              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    value={method.name}
                                    onChange={(e) => updateShippingMethod(index, 'name', e.target.value)}
                                    placeholder="Shipping method name..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                  />
                                </div>
                                <div className="w-24">
                                  <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                                    <input
                                      type="number"
                                      value={method.price}
                                      onChange={(e) => updateShippingMethod(index, 'price', parseFloat(e.target.value) || 0)}
                                      placeholder="0.00"
                                      min="0"
                                      step="0.01"
                                      className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeShippingMethod(index)}
                                  className="text-red-600 hover:text-red-700 p-1"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Save Button */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={saveShippingSettings}
                          disabled={isSavingShipping}
                          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingShipping ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Shipping Settings'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Management Section */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <ShoppingCart className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Order Management</h3>
                      <p className="text-sm text-gray-600">Handle orders and shipping</p>
                    </div>
                  </div>
                  <button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    Manage Orders
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
